import { useEffect, useRef, useState } from "react";
import Column from "@amzn/meridian/esm/components/column";
import Loader from "@amzn/meridian/esm/components/loader";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Box from "@amzn/meridian/esm/components/box";
import Theme from "@amzn/meridian/esm/components/theme";
import "../legacy/inMeeting/summary/Summaries.css";
import BackButton from "../../components/BackButton";
import StartedByStatus from "../../components/StartedByStatus";
import SummaryCard from "../../components/summary/SummaryCard";
import { fetchPaginatedSummaries } from "../../utils/summaryUtils";
import aimsTheme from "../../styles/meridian-theme-aims.json";
import { SESSION_STORAGE_KEYS } from "../../constants";
import { THEME_COLOR } from "../../styles/colors";
import LogManager from "../../utils/logManager";
import NoSummary from "../../components/summary/NoSummary";
import Loading from "../../components/Loading";
import AlertModal, { ALERT_VARIANT } from "../../components/alertModal/AlertModal";

const SCROLL_THRESHOLD = 300;

/**
 * Summaries - Summary list view for the in-meeting assistant.
 * Copied from inMeeting/summary/Summaries.jsx but uses onSelectSummary callback
 * instead of route navigation.
 */
const Summaries = ({ onSelectSummary, onBack, isSessionActive, startedBy }) => {
  const [isSummaryEmpty, setIsSummaryEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showServerError, setShowServerError] = useState(false);
  const [summaryGroups, setSummaryGroups] = useState(() => {
    const cached = sessionStorage.getItem(SESSION_STORAGE_KEYS.SUMMARY_CACHE);
    return cached && cached !== "undefined" ? JSON.parse(cached) : {};
  });
  const [rawSummaries, setRawSummaries] = useState(() => {
    const cached = sessionStorage.getItem(SESSION_STORAGE_KEYS.RAW_SUMMARIES);
    return cached && cached !== "undefined" ? JSON.parse(cached) : [];
  });
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(() => {
    const cached = sessionStorage.getItem(SESSION_STORAGE_KEYS.LAST_EVALUATED_KEY);
    return cached && cached !== "undefined" ? JSON.parse(cached) : null;
  });
  const [isEndOfData, setIsEndOfData] = useState(
    sessionStorage.getItem(SESSION_STORAGE_KEYS.IS_END_OF_DATA) === "true"
  );

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    LogManager.info("User opened Summaries view");
    
    const hasCachedData = Object.keys(summaryGroups).length > 0;
    
    if (hasCachedData) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    fetchPaginatedSummaries(
      true,
      null,
      5,
      {
        setSummaryGroups,
        setRawSummaries,
        setLastEvaluatedKey,
        setIsEndOfData
      }
    )
      .catch((error) => {
        console.error("Failed to load summaries:", error);
        setShowServerError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const hasSummaries = Object.keys(summaryGroups).length > 0;
    setIsSummaryEmpty(!hasSummaries);
  }, [summaryGroups]);

  const handleScroll = (evt) => {
    if (isLoadingMore || isEndOfData) return;

    const target = evt.target;
    const distanceFromBottom = target.scrollHeight - target.clientHeight - target.scrollTop;

    if (distanceFromBottom <= SCROLL_THRESHOLD) {
      setIsLoadingMore(true);
      
      fetchPaginatedSummaries(
        false, 
        lastEvaluatedKey, 
        10, 
        {
          setSummaryGroups,
          setRawSummaries,
          setLastEvaluatedKey,
          setIsEndOfData
        }
      )
        .catch((error) => {
          console.error("Failed to load more summaries:", error);
          setShowServerError(true);
        })
        .finally(() => {
          setIsLoadingMore(false);
        });
    }
  };

  const onViewMoreClick = (summaryData) => {
    onSelectSummary(summaryData);
  };

  return (
    <Column spacing="none" alignmentHorizontal="center" height={isSessionActive ? "calc(100vh - 131px)" : "calc(100vh - 75px)"}>
      <Column spacing="300" spacingInset="300 none none none" width="304px">
        <Row alignmentHorizontal="start">
          <BackButton onClick={onBack} />
        </Row>
        {startedBy && <StartedByStatus startedBy={startedBy} />}
      </Column>
      {isLoading ? (
        <Loading />
      ) : showServerError ? (
        <AlertModal variant={ALERT_VARIANT.COMMON_ERROR} isOpen={showServerError} onClose={() => setShowServerError(false)} />
      ) : isSummaryEmpty ? (
        <NoSummary />
      ) : (
        <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.FROST_GRAY }}>
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{
              width: '362px',
              overflowY: 'auto',
            }}
          >
            <Column spacing="none" alignmentHorizontal="center">
                <Box height="auto" spacing="none" spacingInset="none">
                  <Column spacing="none" spacingInset="none">
                    {Object.entries(summaryGroups).map(([groupTitle, summaries]) => (
                      <Box key={groupTitle} marginBottom="space100">
                        <Column height="16px" />
                        <Text type="h100">{groupTitle}</Text>
                        <Column height="16px" spacing="none" spacingInset="none" />
                        <Column spacing="none" spacingInset="none">
                          {summaries.map((summary) => (
                            <Column key={summary.aimsUuid} spacing="none" spacingInset="none">
                              <SummaryCard summary={summary} onViewMoreClick={onViewMoreClick} />
                              <Column height="12px" />
                            </Column>
                          ))}
                        </Column>
                      </Box>
                    ))}

                    {isLoadingMore && (
                      <Box>
                        <Column alignmentHorizontal="center">
                          <Theme palette={aimsTheme}>
                            <Loader size="medium" />
                          </Theme>
                        </Column>
                      </Box>
                    )}

                    {isEndOfData && rawSummaries.length > 0 && (
                      <Box>
                        <Column alignmentHorizontal="center">
                          <Text color="secondary">No more summary</Text>
                        </Column>
                      </Box>
                    )}
                  </Column>
                </Box>
            </Column>
          </div>
        </Theme>
      )}
    </Column>
  );
};

export default Summaries;
